import { useMemo, useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { bomSettings, cutPlan } from "@/lib/bom";
import { exportXlsx } from "@/lib/xlsx";
import { BomSettingsBar } from "@/components/bom/BomSettingsBar";
import { QuotationTab, MaterialsTab, HardwareTab } from "@/components/bom/BomTables";
import { CutPlanTab } from "@/components/bom/CutPlanTab";

const TABS = [["quotation", "Quotation"], ["bom", "BOM"], ["cuts", "Cut plan"], ["hardware", "Hardware order"]];

export const BomDialog = ({ items, tb, setTb, onClose }) => {
  const s = bomSettings(tb);
  const plan = useMemo(() => cutPlan(items, s), [items, s.bar, s.kerf, s.trim, s.mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const setS = (patch) => setTb({ ...tb, bom: { ...(tb.bom || {}), ...patch } });
  const xlsx = async () => {
    setBusy(true);
    try { await exportXlsx(items, tb, s); toast.success("Excel exported · Quotation, BOM, Cut plan, Hardware order"); } catch (e) { console.error(e); toast.error("Excel export failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[1280px] w-[96vw] h-[90vh] flex flex-col gap-4 bg-[#111827] border-slate-700 text-slate-100 p-5" data-testid="bom-dialog">
        <DialogHeader className="flex-row items-start gap-4 space-y-0 pr-8">
          <div className="flex-1">
            <DialogTitle className="font-heading text-lg" data-testid="bom-dialog-title">Project BOM, cut plan & hardware order</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs mt-1">{items.length} items · {items.reduce((a, it) => a + (it.quantity || 1), 0)} units · quantities include each item's quantity</DialogDescription>
          </div>
          <button data-testid="bom-export-xlsx-btn" onClick={xlsx} disabled={busy}
            className="h-9 px-3 rounded-md bg-emerald-500 text-slate-950 text-xs font-semibold flex items-center gap-1.5 hover:bg-emerald-400 transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}Excel (.xlsx)
          </button>
        </DialogHeader>
        <BomSettingsBar raw={tb.bom || {}} s={s} setS={setS} plan={plan} />
        <Tabs defaultValue="quotation" className="flex-1 min-h-0 flex flex-col">
          <TabsList className="h-10 w-fit bg-[#0d1220] border border-slate-800 p-1">
            {TABS.map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`bom-tab-${v}`} className="text-xs font-mono uppercase tracking-wider data-[state=active]:bg-slate-800 data-[state=active]:text-cyan-300">{l}</TabsTrigger>
            ))}
          </TabsList>
          <div className="flex-1 min-h-0 overflow-auto mt-3 pr-1">
            <TabsContent value="quotation" className="mt-0"><QuotationTab items={items} s={s} /></TabsContent>
            <TabsContent value="bom" className="mt-0"><MaterialsTab items={items} plan={plan} /></TabsContent>
            <TabsContent value="cuts" className="mt-0"><CutPlanTab plan={plan} s={s} /></TabsContent>
            <TabsContent value="hardware" className="mt-0"><HardwareTab items={items} /></TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
